import type { CardChange } from '../components/qagent/document-viewer/card-changes';
import { get, post, remove, patch } from "../http-client/client";
import { ApiResponse } from "../http-client/Response";
import { useBoundStore } from "../store";

// ============================================================================
// INTERFACES
// ============================================================================

export interface QAgentChat {
  id: string;
  title: string | null;
  userId: string;
  projectId: string | null;
  /** 'email' for conversations started by the email agent */
  origin?: 'email' | null;
  /** Documents are in an internal email container, not a user project */
  projectHidden?: boolean;
  contractId: string | null;
  /** Project file the generated contract was saved to */
  contractFileId: string | null;
  /** Set when the chat continues a comparison */
  comparisonId?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface QAgentChatPreview extends QAgentChat {
  messageCount: number;
  lastMessage?: string;
  projectName?: string;
}

export interface QAgentMessage {
  id: string;
  content: string;
  type: 'ai' | 'human' | 'tool';
  toolCalls?: any[];
  citations?: QAgentCitation[];
  /** Documents the model saw only in part (this answer; not stored) */
  notices?: QAgentDocumentNotice[];
  createdAt?: string;
  /** Set on a comparison card: the comparison it shows (there is no text) */
  comparisonId?: string;
}

/** Sent when the model does not get a whole document */
export type QAgentDocumentNotice =
  | { code: 'file_truncated'; fileName: string; shownChars: number; totalChars: number }
  | { code: 'file_summarised'; fileName: string };

export interface QAgentCitation {
  /** Number of the source in the answer text, [index]; unique across all searches of one answer */
  index?: number;
  sourceType?: 'document' | 'web' | 'knowledge';
  fileName: string;
  chunkText: string;
  uri?: string;
  retrievedAt?: string;
}

export interface QAgentSuggestion {
  id: string;
  chatId: string;
  fileId: string;
  type: 'edit' | 'insert' | 'delete';
  oldText: string | null;
  newText: string | null;
  comment: string;
  status: 'pending' | 'accepted' | 'rejected';
  paragraphIndex: number;
  textBefore: string | null;
  textAfter: string | null;
  sectionTitle: string | null;
  exportedAt?: Date | null;
  /** Edits: the changes the Word redline makes, for the viewer to draw exactly */
  changes?: CardChange[] | null;
  fileVersion?: number | null;
  fileName?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// REQUEST DTOS
// ============================================================================

export interface CreateQAgentChatDto {
  title?: string;
  projectId?: string;
}

export interface StreamQAgentMessageDto {
  message: string;
  playbookId?: string;
  fileIds?: string[];
  skillId?: string;
}

export interface AttachProjectDto {
  projectId?: string | null;
}

// ============================================================================
// API SERVICE FUNCTIONS
// ============================================================================

/**
 * Create a new QAgent chat
 */
export const createQAgentChat = (
  data?: CreateQAgentChatDto
): Promise<ApiResponse<QAgentChat>> => {
  return post<QAgentChat>("/qagent/chats", data || {}, null);
};

/**
 * Get all QAgent chats for the current user
 */
export const getQAgentChats = (): Promise<ApiResponse<QAgentChatPreview[]>> => {
  return get<QAgentChatPreview[]>("/qagent/chats", null);
};

/**
 * Get a specific QAgent chat by ID
 */
export const getQAgentChat = (chatId: string): Promise<ApiResponse<QAgentChat>> => {
  return get<QAgentChat>(`/qagent/chats/${chatId}`, null);
};

/**
 * Delete a QAgent chat
 */
export const deleteQAgentChat = (chatId: string): Promise<ApiResponse<void>> => {
  return remove<void>(`/qagent/chats/${chatId}`, null);
};

/**
 * Clear chat context (conversation memory) without deleting the chat
 */
export const clearQAgentChatContext = (chatId: string): Promise<ApiResponse<void>> => {
  return post<void>(`/qagent/chats/${chatId}/clear`, {}, null);
};

/**
 * Attach or detach a project from a chat
 */
export const attachProjectToChat = (
  chatId: string,
  projectId: string | null
): Promise<ApiResponse<QAgentChat>> => {
  return patch<QAgentChat>(`/qagent/chats/${chatId}/project`, { projectId }, null);
};

/**
 * Get messages in a QAgent chat
 */
export const getQAgentMessages = (
  chatId: string
): Promise<ApiResponse<QAgentMessage[]>> => {
  return get<QAgentMessage[]>(`/qagent/chats/${chatId}/messages`, null);
};

/** Show a comparison in the chat as a card (it stays when the chat is reopened) */
export const addComparisonCard = (
  chatId: string,
  comparisonId: string
): Promise<ApiResponse<QAgentMessage>> => {
  return post<QAgentMessage>(`/qagent/chats/${chatId}/comparisons`, { comparisonId }, null);
};

/**
 * Stream a message in a QAgent chat (SSE)
 */
/**
 * Get all suggestions for a chat
 */
export const getQAgentSuggestions = (
  chatId: string
): Promise<ApiResponse<QAgentSuggestion[]>> => {
  return get<QAgentSuggestion[]>(`/qagent/chats/${chatId}/suggestions`, null);
};

/**
 * Get pending suggestions for a chat
 */
export const getPendingSuggestions = (
  chatId: string
): Promise<ApiResponse<QAgentSuggestion[]>> => {
  return get<QAgentSuggestion[]>(`/qagent/chats/${chatId}/suggestions/pending`, null);
};

/**
 * Update suggestion status (accept/reject)
 */
export const updateSuggestionStatus = (
  suggestionId: string,
  status: 'accepted' | 'rejected'
): Promise<ApiResponse<QAgentSuggestion>> => {
  return patch<QAgentSuggestion>(`/qagent/suggestions/${suggestionId}`, { status }, null);
};

/**
 * Accept all pending suggestions in a chat
 */
export const acceptAllSuggestions = (
  chatId: string
): Promise<ApiResponse<{ affected: number }>> => {
  return post<{ affected: number }>(`/qagent/chats/${chatId}/suggestions/accept-all`, {}, null);
};

/**
 * Reject all pending suggestions in a chat
 */
export const rejectAllSuggestions = (
  chatId: string
): Promise<ApiResponse<{ affected: number }>> => {
  return post<{ affected: number }>(`/qagent/chats/${chatId}/suggestions/reject-all`, {}, null);
};

export interface ExportFileResult {
  downloadUrl: string;
  fileName: string;
  /** Suggestions written into the document */
  applied?: number;
  /** Accepted suggestions that could not be applied (left out of the file) */
  skipped?: Array<{ suggestionId: string; type: string; reason: string }>;
}

/**
 * 'redline': accepted suggestions as Word tracked changes (optionally with the
 * reasoning as comments); nothing is saved. 'clean': the changes applied and
 * saved as a new version of the file.
 */
export interface ExportFileOptions {
  format: 'redline' | 'clean';
  comments?: boolean;
}

/**
 * Export a file with its accepted suggestions
 */
export const exportFileWithChanges = (
  chatId: string,
  fileId: string,
  options: ExportFileOptions = { format: 'clean' }
): Promise<ApiResponse<ExportFileResult>> => {
  return post<ExportFileResult>(
    `/qagent/chats/${chatId}/files/${fileId}/export`,
    options,
    null
  );
};

/**
 * Undo an accepted suggestion - set it back to pending.
 */
export const undoSuggestion = (
  suggestionId: string
): Promise<ApiResponse<QAgentSuggestion>> => {
  return post<QAgentSuggestion>(`/qagent/suggestions/${suggestionId}/undo`, {}, null);
};

/**
 * Get accepted (non-exported) changes for a file in a chat.
 */
export const getFileChanges = (
  chatId: string,
  fileId: string
): Promise<ApiResponse<{ changes: QAgentSuggestion[]; totalChanges: number }>> => {
  return get<{ changes: QAgentSuggestion[]; totalChanges: number }>(
    `/qagent/chats/${chatId}/files/${fileId}/changes`,
    null
  );
};

/**
 * Get version history for a file.
 */
export const getFileVersions = (
  fileId: string
): Promise<ApiResponse<any[]>> => {
  return get<any[]>(`/qagent/files/${fileId}/versions`, null);
};

/**
 * Export a generated contract as a DOCX project file.
 * Returns download URL, file name, and file ID.
 */
export interface ContractExportResult {
  downloadUrl: string;
  fileName: string;
  fileId: string;
  projectId: string;
  projectName: string | null;
  /** True when the chat had no project and one was created for the contract */
  projectCreated: boolean;
}

/**
 * Save the chat's generated contract as a DOCX in its project, creating a
 * project if the chat has none. Idempotent: returns the existing file if the
 * contract was already saved.
 */
export const exportContractAsProjectFile = (
  chatId: string,
): Promise<ApiResponse<ContractExportResult>> => {
  return post<ContractExportResult>(
    `/qagent/chats/${chatId}/contract/export`,
    {},
    null,
  );
};

/**
 * Link a contract to a chat (persists contractId on the chat entity)
 */
export const linkContractToChat = (
  chatId: string,
  contractId: string,
): Promise<ApiResponse<QAgentChat>> => {
  return patch<QAgentChat>(`/qagent/chats/${chatId}/contract/${contractId}`, {}, null);
};

/**
 * Get or create a chat for a project.
 * Each project has exactly one chat.
 */
export const getOrCreateProjectChat = (
  projectId: string
): Promise<ApiResponse<QAgentChat>> => {
  return get<QAgentChat>(`/qagent/projects/${projectId}/chat`, null);
};

/**
 * Stream a message in a QAgent chat (SSE)
 */
/**
 * Refresh the access token using the refresh token.
 * Returns the new access token, or null if refresh failed.
 */
const refreshAccessToken = async (): Promise<string | null> => {
  const { refreshToken, setAccessToken, setRefreshToken, setUser } =
    useBoundStore.getState();

  if (!refreshToken) return null;

  try {
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const response = await fetch(`${baseUrl}/auth/token`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
    });

    if (!response.ok) {
      // Refresh token is invalid - clear auth state
      setAccessToken('');
      setRefreshToken('');
      setUser(null);
      return null;
    }

    const result = await response.json();
    const newAccessToken = result.data?.accessToken || result.accessToken;
    const newRefreshToken = result.data?.refreshToken || result.refreshToken;

    if (newAccessToken) {
      setAccessToken(newAccessToken);
      if (newRefreshToken) setRefreshToken(newRefreshToken);
      return newAccessToken;
    }
    return null;
  } catch {
    return null;
  }
};

export const streamQAgentMessage = async (
  chatId: string,
  data: StreamQAgentMessageDto,
  onChunk: (chunk: string) => void,
  onCitations?: (citations: QAgentCitation[]) => void,
  onError?: (error: string) => void,
  onComplete?: () => void,
  signal?: AbortSignal,
  onToolStatus?: (toolName: string, status: 'start' | 'end') => void,
  onNotice?: (notice: QAgentDocumentNotice) => void,
): Promise<void> => {
  const baseUrl = process.env.REACT_APP_API_URL || '';
  const url = `${baseUrl}/qagent/chats/${chatId}/stream`;

  // Get token from Zustand store (always fresh)
  let accessToken = useBoundStore.getState().accessToken;

  if (!accessToken) {
    onError?.('Not authenticated');
    return;
  }

  const doStream = async (token: string): Promise<Response> => {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
      signal,
    });
  };

  const MAX_NETWORK_RETRIES = 2;
  const RETRY_DELAYS = [1000, 3000];

  const attemptStream = async (): Promise<Response> => {
    for (let attempt = 0; attempt <= MAX_NETWORK_RETRIES; attempt++) {
      try {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        let response = await doStream(accessToken);

        // On 401, try refreshing the token and retry once
        if (response.status === 401) {
          const newToken = await refreshAccessToken();
          if (!newToken) {
            throw new Error('Session expired. Please log in again.');
          }
          accessToken = newToken;
          response = await doStream(accessToken);
        }

        if (!response.ok) {
          const text = await response.text();
          console.error('[QAgent] Error response:', text);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response;
      } catch (error: any) {
        // Only retry on network errors (TypeError from fetch), not HTTP errors
        const isNetworkError = error instanceof TypeError;
        const isRetryable = isNetworkError && attempt < MAX_NETWORK_RETRIES;

        if (!isRetryable || signal?.aborted) throw error;
        console.warn(`[QAgent] Network error, retrying in ${RETRY_DELAYS[attempt]}ms (attempt ${attempt + 1}/${MAX_NETWORK_RETRIES})`);
        await new Promise(r => setTimeout(r, RETRY_DELAYS[attempt]));
      }
    }
    throw new Error('Failed to connect after retries');
  };

  try {
    const response = await attemptStream();

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      if (signal?.aborted) {
        reader.cancel();
        break;
      }
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const eventData = JSON.parse(line.slice(6));
            if (eventData.event === 'on_chat_model_stream' && eventData.content) {
              onChunk(eventData.content);
            } else if (eventData.event === 'on_tool_status' && eventData.content) {
              try {
                const statusData = JSON.parse(eventData.content);
                onToolStatus?.(statusData.toolName, statusData.status);
              } catch { /* ignore parse errors */ }
            } else if (eventData.event === 'on_citations' && eventData.content) {
              const citations = JSON.parse(eventData.content);
              onCitations?.(citations);
            } else if (eventData.event === 'on_notice' && eventData.content) {
              onNotice?.(JSON.parse(eventData.content));
            } else if (eventData.type === 'error') {
              onError?.(eventData.message || 'An error occurred');
            }
          } catch (e) {
            console.warn('[QAgent] Failed to parse SSE line:', line, e);
          }
        }
      }
    }

    // Aborted by the caller (stop button or leaving the chat): it handles the
    // partial content itself
    if (signal?.aborted) return;
    onComplete?.();
  } catch (error: any) {
    if (signal?.aborted) return;

    // Stream interrupted - try to recover the completed message from the checkpoint
    console.warn('[QAgent] Stream interrupted, attempting recovery...', error.message);
    try {
      await new Promise((r) => setTimeout(r, 2000));
      const latestToken = useBoundStore.getState().accessToken;
      if (latestToken) {
        const recoveryUrl = `${baseUrl}/qagent/chats/${chatId}/messages/latest`;
        const recovery = await fetch(recoveryUrl, {
          headers: { Authorization: `Bearer ${latestToken}` },
        });
        if (recovery.ok) {
          const msg = await recovery.json();
          if (msg?.content) {
            onChunk(msg.content);
            onComplete?.();
            return;
          }
        }
      }
    } catch (recoveryError) {
      console.warn('[QAgent] Recovery failed:', recoveryError);
    }

    onError?.(error.message || 'Failed to stream message');
  }
};

/** Rename one of your chats */
export const renameQAgentChat = (
  chatId: string,
  title: string,
): Promise<ApiResponse<QAgentChat>> =>
  patch<QAgentChat>(`/qagent/chats/${chatId}/title`, { title }, null);
